import { useEffect, useState } from "react";
import type {
  Profile,
  ProfileLinkConfig,
  SkillCategoryBlock,
  SkillCount,
  SkillTag,
  UserProfile,
} from "../../../../../client";

import { EditableSkillTag } from "./EditableSkillTag";
import { AddSkillButton } from "./AddSkillButton";

interface Props {
  editable: boolean;
  skillCategoryBlocks?: SkillCategoryBlock[];
  hideIcons?: boolean;
  pdfMode?: boolean;
  /** When provided, chips become inline-removable in the template editor. */
  onRemoveSkill?: (skillName: string) => void;
  /** When provided, an inline "Add skill" button opens the skill picker. */
  onAddSkill?: () => void;
  /** Layout: false (default) → skill cloud (flex-wrap chips); true → vertical
   *  list, one skill per line. */
  asList?: boolean;
  /** Ordering: "" (alphabetical, default) or "mostMentioned". */
  order?: string;
  /** name (lowercased) → mention count, used when order is "mostMentioned". */
  skillCounts?: Record<string, number>;
}

export const SkillsBlock = ({
  editable,
  skillCategoryBlocks,
  hideIcons,
  pdfMode,
  onRemoveSkill,
  onAddSkill,
  asList,
  order,
  skillCounts,
}: Props) => {
  const [allSkillTags, setAllSkillTags] = useState<SkillTag[]>([]);

  const compare = (a: SkillTag, b: SkillTag) => {
    if (order === "mostMentioned") {
      const ca = skillCounts?.[a.name.toLowerCase()] ?? 0;
      const cb = skillCounts?.[b.name.toLowerCase()] ?? 0;
      if (cb !== ca) return cb - ca; // most mentioned first
    }
    return a.name.localeCompare(b.name);
  };

  return (
    <div
      className={
        asList
          ? "flex flex-col items-start w-full gap-1"
          : "flex flex-row flex-wrap w-full gap-1"
      }
    >
      {skillCategoryBlocks
        ?.flatMap((block) => block.skillTags.map((skillTag) => skillTag))
        ?.sort(compare)
        .map((skillSummary) => {
          return (
            <EditableSkillTag
              key={skillSummary.name}
              skillTag={skillSummary}
              hideIcon={hideIcons ?? false}
              pdfMode={pdfMode}
              onRemove={onRemoveSkill}
            />
          );
        })}
      {onAddSkill && <AddSkillButton onClick={onAddSkill} />}
    </div>
  );
};
